// Calor manufacturer -> manufacturer's code lookup.
//
// Source of truth: backend/assets/calor/Calor_Manufacturer_Codes.pdf
// (kept out of the public web root -- see that folder's README).
//
// Each entry pairs a Calor manufacturer's code with one or more names that
// share that code.  In the source document a single code can list several
// names on one row, separated by commas, "or", "&" or "/".  Those are all
// captured as aliases below so any of them resolves to the same code.
//
// Schneider is intentionally split into two entries (UK vs France) because
// the two factories share almost the same spelling but have different codes.
// Callers must disambiguate with the user before a code can be assigned --
// see `resolveManufacturerCode` / `isSchneiderAmbiguous`.

export const MANUFACTURER_CODES = [
  { code: '21', names: ['A.M.F', 'Beraid', 'Euroweld'] },
  { code: '38', names: ['American Welding Tank'] },
  { code: '56', names: ['Autogas 2000 (Tartarini)', 'Autogas 2000', 'Tartarini'] },
  { code: '53', names: ['Bagom'] },
  { code: '26', names: ['Butterfiled', 'Butterfield'] },
  { code: '39', names: ['Calor'] },
  { code: '48', names: ['Charles Roberts'] },
  { code: '29', names: ['Classman'] },
  { code: '9', names: ['Clayton'] },
  { code: '1', names: ['Comet'] },
  { code: '49', names: ['Cool Technology'] },
  { code: '27', names: ['Cylindric / Denmark', 'Cylindric', 'Denmark'] },
  { code: '33', names: ['Davidson', 'Howden-Sirocco', 'Howden Sirocco'] },
  { code: '43', names: ['Deepdale Engineering'] },
  { code: '12', names: ['Dempster'] },
  { code: '36', names: ['Firth Blakeley'] },
  { code: '7', names: ['Grazebrook'] },
  { code: '40', names: ['Halliburton'] },
  { code: '13', names: ['Harvey, G.A', 'Harvey', 'G.A Harvey'] },
  { code: '34', names: ['Holmes, W.C', 'Holmes', 'W.C Holmes'] },
  { code: '14', names: ['Horseley Bridge'] },
  { code: '24', names: ['Ideal'] },
  { code: '6', names: ['Jenkins'] },
  { code: '32', names: ['Kings'] },
  { code: '47', names: ['Liotard Freres'] },
  { code: '35', names: ['Motherwell Bridge', 'M.B.F'] },
  { code: '19', names: ['Mannesman'] },
  { code: '4', names: ['Masters'] },
  { code: '10', names: ['Mechans'] },
  { code: '8', names: ['Metal Formations'] },
  { code: '42', names: ['Metallisation'] },
  { code: '11', names: ['Old Park Engineering'] },
  { code: '22', names: ['Parsons. C.A', 'Parsons', 'C.A Parsons'] },
  { code: '51', names: ['Power Gas'] },
  { code: '23', names: ['Pumps'] },
  { code: '37', names: ['PX Nuclear'] },
  { code: '3', names: ['Robert Bros'] },
  { code: '2', names: ['Rubery Owen'] },
  // Schneider -- ambiguous pair, disambiguated by country (see below).
  { code: '45', names: ['Schneider Industry (UK)', 'Schneider Industry UK'], schneider: 'uk' },
  { code: '28', names: ['Schneider Industrie (France)', 'Schneider Industrie France'], schneider: 'france' },
  { code: '17', names: ['Siebel'] },
  { code: '41', names: ['Sleeman'] },
  { code: '44', names: ['South Staffs'] },
  { code: '54', names: ['Stag', 'Flamco'] },
  { code: '50', names: ['Steel Fabrications'] },
  { code: '15', names: ['Steel Process'] },
  { code: '5', names: ['Thompson', 'John Thompson'] },
  { code: '20', names: ['Unknown'] },
  { code: '31', names: ['Van Leer'] },
  { code: '46', names: ['Vayland'] },
  { code: '52', names: ['Veirod & woods', 'Veirod', 'Woods'] },
  { code: '30', names: ['Wefco'] },
  { code: '16', names: ['Whessoe'] },
  { code: '57', names: ['Witte van moort'] },
  { code: '18', names: ['Wrights'] },
  { code: '25', names: ['Yorkshire Engineering'] },
];

// The two Schneider options offered to the user in the confirmation modal.
export const SCHNEIDER_OPTIONS = [
  { code: '45', label: 'Schneider Industry (UK)', country: 'uk' },
  { code: '28', label: 'Schneider Industrie (France)', country: 'france' },
];

// Normalise a name for comparison: lower-case, strip anything that isn't a
// letter or digit down to single spaces.  "Schneider Industry (UK)" ->
// "schneider industry uk"; "A.M.F" -> "a m f".
const normalize = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

// Collapse spaces entirely -- used for the fuzzy Schneider spelling test so
// "schnieder", "schneider", "schnider" all reduce to a comparable token.
const compact = (value) => normalize(value).replace(/\s+/g, '');

// True when the input reads like "Schneider" regardless of the common
// mis-spelling (schnieder) or trailing factory/country words.
export const looksLikeSchneider = (value) => /schn[a-z]*der/.test(compact(value));

// Given a raw manufacturer name string, work out the Calor code.
//
// Returns one of:
//   { status: 'matched',   code, matchedName }
//   { status: 'ambiguous', options: SCHNEIDER_OPTIONS }   // needs user choice
//   { status: 'none' }                                    // no code found
export const resolveManufacturerCode = (rawName) => {
  const norm = normalize(rawName);
  if (!norm) return { status: 'none' };

  // Schneider handling first: the spelling is easy to get wrong and the two
  // factories share a near-identical name but different codes.
  if (looksLikeSchneider(norm)) {
    const hasUK = /\b(uk|united kingdom|england|britain|british|gb)\b/.test(norm);
    const hasFrance = /\b(france|french|fr|fra)\b/.test(norm);
    if (hasUK && !hasFrance) {
      return { status: 'matched', code: '45', matchedName: 'Schneider Industry (UK)' };
    }
    if (hasFrance && !hasUK) {
      return { status: 'matched', code: '28', matchedName: 'Schneider Industrie (France)' };
    }
    return { status: 'ambiguous', options: SCHNEIDER_OPTIONS };
  }

  // Exact alias match (normalised).  Aliases already cover comma / "or" / "&"
  // / "/" separated names from the source document.
  for (const entry of MANUFACTURER_CODES) {
    for (const alias of entry.names) {
      if (normalize(alias) === norm) {
        return { status: 'matched', code: entry.code, matchedName: alias };
      }
    }
  }

  // Fallback: whole-word containment either direction, so "Charles Roberts
  // Ltd" still resolves to "Charles Roberts".  Skip very short aliases to
  // avoid accidental substring hits.
  for (const entry of MANUFACTURER_CODES) {
    if (entry.schneider) continue; // Schneider only ever matches above.
    for (const alias of entry.names) {
      const aliasNorm = normalize(alias);
      if (aliasNorm.length < 4) continue;
      const paddedInput = ` ${norm} `;
      const paddedAlias = ` ${aliasNorm} `;
      if (paddedInput.includes(paddedAlias) || paddedAlias.includes(paddedInput)) {
        return { status: 'matched', code: entry.code, matchedName: alias };
      }
    }
  }

  return { status: 'none' };
};
